// Shown inside the admin inspection: damage found on earlier checks that hasn't been recorded as repaired.
// For each, the inspector says it is "Still there" (which records it again on this check, so its history carries on)
// or "Repaired" (which records the repair). Nothing here is required to finish the inspection.
import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { Check, Wrench } from "lucide-react";
import { formatDate } from "@/lib/dates";
import { buildDamageRegister, severityLabel, viewLabel, type DamageItem } from "@/lib/damageRegister";
import { loadDamageData, markDamageRepaired, type FormMarker } from "@/lib/damageData";
import { loggedInUserName } from "@/lib/vehicleChecksData";
import { localIsoDay } from "@/lib/vehicleChecks";

interface Props {
  vehicleNumber: string;
  /** This inspection's id, so its own marks are never offered back as "earlier damage". */
  inspectionId: string;
  /** Photo ids on this inspection by photo type; a mark can only be placed once that side's photo has been taken. */
  photoIds: Record<string, string>;
  /** Marks currently on this inspection, by photo type. */
  markers: Record<string, FormMarker[]>;
  onStillThere: (view: string, marker: FormMarker) => void;
}

const THIS_SPOT = 5;

const KnownDamagePanel: React.FC<Props> = ({ vehicleNumber, inspectionId, photoIds, markers, onStillThere }) => {
  const [items, setItems] = useState<DamageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [repairing, setRepairing] = useState<string | null>(null);
  const [repairDate, setRepairDate] = useState(() => localIsoDay(new Date()));

  const load = useCallback(async () => {
    try {
      const data = await loadDamageData({ vehicle: vehicleNumber });
      const earlier = data.sightings.filter((s) => s.inspection_id !== inspectionId);
      setItems(buildDamageRegister(earlier).filter((i) => i.status === "present"));
    } catch (e) {
      console.error("Could not load earlier damage", e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [vehicleNumber, inspectionId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading || items.length === 0) return null;

  const recordedHere = (item: DamageItem): boolean =>
    (markers[item.view] ?? []).some(
      (m) =>
        m.damage_type.trim().toLowerCase() === item.damageType.trim().toLowerCase() &&
        Math.abs(Number(m.x_percent) - item.x) < THIS_SPOT * 2 &&
        Math.abs(Number(m.y_percent) - item.y) < THIS_SPOT * 2
    );

  const confirmRepair = async (item: DamageItem) => {
    try {
      await markDamageRepaired(item.markerIds, repairDate, loggedInUserName());
      toast({ title: "Repair recorded", description: `${viewLabel(item.view)} ${item.damageType.toLowerCase()} marked as repaired.` });
      setRepairing(null);
      await load();
    } catch (e: any) {
      toast({ title: "Couldn't record the repair", description: e?.message ?? String(e), variant: "destructive" });
    }
  };

  return (
    <div className="rounded-lg border border-amber-700/50 bg-amber-950/20 p-4">
      <div className="mb-1 text-sm font-semibold text-amber-400">Damage from earlier checks ({items.length})</div>
      <p className="mb-3 text-xs text-gray-400">
        For each, say whether it is still there (it is recorded again on this check) or has been repaired. Mark any new damage on the photos below.
      </p>
      <div className="space-y-2">
        {items.map((item) => {
          const last = item.sightings[item.sightings.length - 1];
          const here = recordedHere(item);
          const hasPhoto = !!photoIds[item.view];
          return (
            <div key={item.key} className="rounded-md border border-gray-700 bg-gray-900/60 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm text-gray-200">
                  <span className="font-medium">
                    {viewLabel(item.view)} &middot; {item.damageType}
                  </span>
                  <span className="ml-2 text-xs text-gray-400">
                    {severityLabel(item.latestSeverity)} &middot; first seen {formatDate(item.firstSeenAt)} &middot; seen {item.sightings.length}&times;
                    {item.reappeared ? " · came back after repair" : ""}
                  </span>
                  {last.notes ? <div className="mt-0.5 text-xs text-gray-400">{last.notes}</div> : null}
                </div>

                {repairing === item.key ? (
                  <div className="flex items-center gap-2">
                    <Input type="date" value={repairDate} max={localIsoDay(new Date())} onChange={(e) => setRepairDate(e.target.value)} className="h-8 w-40" />
                    <Button size="sm" onClick={() => confirmRepair(item)} disabled={!repairDate}>
                      Save repair
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setRepairing(null)}>
                      Cancel
                    </Button>
                  </div>
                ) : here ? (
                  <span className="flex items-center gap-1 text-xs text-green-400">
                    <Check className="h-4 w-4" /> Recorded on this check
                  </span>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!hasPhoto}
                      title={hasPhoto ? "Record this damage again on this check" : `Take the ${viewLabel(item.view)} photo first`}
                      onClick={() =>
                        onStillThere(item.view, {
                          x_percent: last.x_percent,
                          y_percent: last.y_percent,
                          damage_type: last.damage_type,
                          severity: (severityLabel(last.severity) || "Minor") as FormMarker["severity"],
                          notes: last.notes ?? "",
                        })
                      }
                    >
                      Still there
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setRepairing(item.key)}>
                      <Wrench className="mr-1 h-3.5 w-3.5" /> Repaired
                    </Button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default KnownDamagePanel;
